import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Heart, MessageSquare, Send, Bookmark, MoreVertical,
  Play, Loader2, PlusCircle, Video, Image as ImageIcon,
  ArrowLeft, X, ChevronRight,
} from 'lucide-react';
import { useFeedStore, Post } from '@/stores/useFeedStore';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getImageUrl } from '@/config/media';

// ─── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000   ? `${(n / 1_000).toFixed(1)}K`
  : String(n);

const isVideoPost = (p: Post) =>
  p.media_type === 'video' || p.media_keys?.some(k => k.includes('videos/'));

const primaryUrl = (p: Post) =>
  p.media_urls?.primary || getImageUrl(p.media_keys?.[0]) || '';

const thumbUrl = (p: Post) =>
  p.media_urls?.thumbnails?.[0] || '';

// ─── Media unavailable placeholder ────────────────────────────────────────────

const MediaUnavailable: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center justify-center gap-1.5 select-none ${compact ? 'aspect-square' : 'h-64'}`}>
    <ImageIcon size={compact ? 20 : 28} className="text-gray-600" />
    <span className="text-gray-500 text-[10px] font-medium tracking-widest uppercase">Unavailable</span>
  </div>
);

// ─── Grid thumbnail card (overview) ───────────────────────────────────────────

const GridCard: React.FC<{ post: Post; onClick: () => void }> = ({ post, onClick }) => {
  const [err, setErr] = useState(false);
  const isVid = isVideoPost(post);
  const url = isVid ? (thumbUrl(post) || primaryUrl(post)) : primaryUrl(post);

  return (
    <button
      onClick={onClick}
      className="relative aspect-square w-full overflow-hidden bg-gray-900 group focus:outline-none"
    >
      {!url || err ? (
        <MediaUnavailable compact />
      ) : (
        <img
          src={url}
          alt=""
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
          onError={() => setErr(true)}
        />
      )}

      {/* video badge */}
      {isVid && !err && (
        <div className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60">
          <Play size={10} className="text-white fill-white" />
        </div>
      )}

      {/* hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="flex items-center gap-3 text-white text-xs font-semibold">
          <span className="flex items-center gap-1">
            <Heart size={13} className="fill-white" /> {fmt(post.likes_count)}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare size={13} /> {fmt(post.comments_count)}
          </span>
        </div>
      </div>
    </button>
  );
};

// ─── Latest posts horizontal strip ────────────────────────────────────────────

const LatestStrip: React.FC<{ posts: Post[]; onSelect: (i: number) => void }> = ({ posts, onSelect }) => {
  const latest = posts.slice(0, 8);
  if (latest.length === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between px-4 mb-2">
        <span className="text-xs font-bold text-light-text dark:text-dark-text uppercase tracking-wider">Latest</span>
        <span className="text-[10px] text-light-subtle dark:text-dark-subtle">Tap to read</span>
      </div>
      <div className="flex gap-2.5 overflow-x-auto px-4 pb-1 scrollbar-hide">
        {latest.map((post, i) => {
          const [err, setErr] = useState(false);
          const isVid = isVideoPost(post);
          const url = isVid ? (thumbUrl(post) || primaryUrl(post)) : primaryUrl(post);
          const avatarUrl = post.avatar_key ? getImageUrl(post.avatar_key) : '';

          return (
            <button
              key={post.id}
              onClick={() => onSelect(i)}
              className="flex-shrink-0 flex flex-col items-center gap-1 group focus:outline-none"
            >
              {/* story-ring style avatar */}
              <div className="w-14 h-14 rounded-full p-0.5 bg-gradient-to-tr from-primary to-golden">
                <div className="w-full h-full rounded-full overflow-hidden bg-gray-900 border-2 border-light-bg dark:border-dark-bg">
                  {!url || err ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <ImageIcon size={14} className="text-gray-600" />
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={() => setErr(true)}
                    />
                  )}
                </div>
              </div>
              <span className="text-[9px] text-light-subtle dark:text-dark-subtle max-w-[56px] truncate">
                {post.username}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Full detail post card (scroll view) ──────────────────────────────────────

const DetailPostMedia: React.FC<{ post: Post }> = ({ post }) => {
  const navigate = useNavigate();
  const [err, setErr] = useState(false);
  const isVid = isVideoPost(post);
  const url = primaryUrl(post);
  const thumb = thumbUrl(post);

  if (!url && !thumb) return <MediaUnavailable />;
  if (err) return <MediaUnavailable />;

  if (isVid) return (
    <div
      className="relative w-full bg-black cursor-pointer"
      onClick={() => navigate('/reels')}
    >
      {thumb
        ? <img src={thumb} alt="" className="w-full max-h-[420px] object-cover" onError={() => setErr(true)} />
        : <video src={url} className="w-full max-h-[420px] object-cover" muted playsInline preload="metadata" />
      }
      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
        <div className="p-3 rounded-full bg-black/50 backdrop-blur-sm">
          <Play size={22} className="text-white fill-white" />
        </div>
      </div>
    </div>
  );

  return (
    <img
      src={url}
      alt="Post"
      className="w-full max-h-[420px] object-cover"
      loading="lazy"
      onError={() => setErr(true)}
    />
  );
};

const DetailPost: React.FC<{ post: Post }> = ({ post }) => {
  const { toggleLike, addComment } = useFeedStore();
  const isLiked = useFeedStore(s => s.isPostLiked(post.id));
  const addCommentLoading = useFeedStore(s => s.addCommentLoading[post.id]);
  const [comment, setComment] = useState('');
  const [expanded, setExpanded] = useState(false);
  const avatarUrl = post.avatar_key ? getImageUrl(post.avatar_key) : '';
  const max = 120;
  const truncate = post.content.length > max && !expanded;

  const submitComment = () => {
    if (!comment.trim() || addCommentLoading) return;
    addComment(post.id, comment);
    setComment('');
  };

  return (
    <article className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-golden flex items-center justify-center flex-shrink-0 overflow-hidden">
            {avatarUrl
              ? <img src={avatarUrl} alt={post.username} className="w-full h-full object-cover" />
              : <span className="text-white text-[10px] font-bold">{post.username?.charAt(0).toUpperCase() || 'U'}</span>
            }
          </div>
          <div>
            <p className="text-xs font-semibold text-light-text dark:text-dark-text leading-tight">{post.username}</p>
            <p className="text-[10px] text-light-subtle dark:text-dark-subtle leading-tight">
              {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <button className="p-1 hover:opacity-60 transition-opacity">
          <MoreVertical size={15} className="text-light-subtle dark:text-dark-subtle" />
        </button>
      </div>

      {/* media */}
      <DetailPostMedia post={post} />

      {/* actions */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-3.5">
          <button onClick={() => toggleLike(post.id)} className="hover:opacity-60 transition-opacity">
            <Heart size={20} className={isLiked ? 'fill-red-500 text-red-500' : 'text-light-text dark:text-dark-text'} />
          </button>
          <button className="hover:opacity-60 transition-opacity">
            <MessageSquare size={20} className="text-light-text dark:text-dark-text" />
          </button>
          <button className="hover:opacity-60 transition-opacity">
            <Send size={20} className="text-light-text dark:text-dark-text rotate-12" />
          </button>
        </div>
        <button className="hover:opacity-60 transition-opacity">
          <Bookmark size={20} className="text-light-text dark:text-dark-text" />
        </button>
      </div>

      {/* likes */}
      <p className="px-3 text-xs font-semibold text-light-text dark:text-dark-text pb-1">
        {fmt(post.likes_count)} likes
      </p>

      {/* caption */}
      <div className="px-3 pb-2">
        <p className="text-xs text-light-text dark:text-dark-text">
          <span className="font-semibold mr-1">{post.username}</span>
          <span className="text-light-subtle dark:text-dark-subtle">
            {truncate ? post.content.substring(0, max) + '…' : post.content}
          </span>
          {truncate && (
            <button onClick={() => setExpanded(true)} className="ml-1 text-light-subtle dark:text-dark-subtle text-[10px]">more</button>
          )}
        </p>
        {post.comments_count > 0 && (
          <p className="text-[10px] text-light-subtle dark:text-dark-subtle mt-0.5">
            View all {fmt(post.comments_count)} comments
          </p>
        )}
      </div>

      {/* comment input */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-light-border dark:border-dark-border">
        <input
          value={comment}
          onChange={e => setComment(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submitComment()}
          placeholder="Add a comment…"
          className="flex-1 bg-transparent outline-none text-xs text-light-text dark:text-dark-text placeholder-light-subtle dark:placeholder-dark-subtle"
        />
        {comment.trim() && (
          <button
            onClick={submitComment}
            disabled={addCommentLoading}
            className="text-primary dark:text-golden text-xs font-semibold disabled:opacity-40"
          >
            {addCommentLoading ? <Loader2 size={11} className="animate-spin" /> : 'Post'}
          </button>
        )}
      </div>
    </article>
  );
};

// ─── Detail scroll view (full-screen overlay) ─────────────────────────────────

const DetailView: React.FC<{ posts: Post[]; startIndex: number; onClose: () => void }> = ({ posts, startIndex, onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { loadMoreFeed, feedPagination, feedLoadingMore } = useFeedStore();

  // Scroll to the tapped post on mount
  useEffect(() => {
    if (!scrollRef.current) return;
    const cards = scrollRef.current.querySelectorAll('[data-post]');
    const target = cards[startIndex] as HTMLElement;
    if (target) target.scrollIntoView({ block: 'start' });
  }, [startIndex]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.85 && feedPagination.has_more && !feedLoadingMore)
      loadMoreFeed();
  }, [feedPagination.has_more, feedLoadingMore, loadMoreFeed]);

  return (
    <div className="fixed inset-0 z-50 bg-light-bg dark:bg-dark-bg flex flex-col">
      {/* sticky header */}
      <div className="flex items-center gap-3 px-4 h-12 border-b border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card shrink-0">
        <button onClick={onClose} className="p-1 hover:opacity-60 transition-opacity">
          <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
        </button>
        <span className="text-sm font-semibold text-light-text dark:text-dark-text">Posts</span>
      </div>

      {/* scrollable posts */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" onScroll={onScroll} style={{ scrollbarWidth: 'none' }}>
        <div className="max-w-sm mx-auto">
          {posts.map((post, i) => (
            <div key={post.id} data-post={i}>
              <DetailPost post={post} />
            </div>
          ))}
          {feedLoadingMore && (
            <div className="flex justify-center py-6">
              <Loader2 size={22} className="animate-spin text-primary dark:text-golden" />
            </div>
          )}
          {!feedPagination.has_more && posts.length > 0 && (
            <div className="text-center py-8">
              <span className="text-xs text-light-subtle dark:text-dark-subtle">You're all caught up 🎉</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Grid overview ─────────────────────────────────────────────────────────────

const GridOverview: React.FC<{ posts: Post[]; onSelect: (i: number) => void }> = ({ posts, onSelect }) => {
  const { feedLoading, feedLoadingMore, feedPagination, loadMoreFeed } = useFeedStore();
  const containerRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.85 && feedPagination.has_more && !feedLoadingMore)
      loadMoreFeed();
  }, [feedPagination.has_more, feedLoadingMore, loadMoreFeed]);

  if (feedLoading && posts.length === 0) return (
    <div className="flex justify-center py-24">
      <Loader2 size={32} className="animate-spin text-primary dark:text-golden" />
    </div>
  );

  if (posts.length === 0) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <div className="w-14 h-14 rounded-full bg-primary/10 dark:bg-golden/10 flex items-center justify-center">
        <Video size={24} className="text-primary dark:text-golden" />
      </div>
      <p className="text-sm font-semibold text-light-text dark:text-dark-text">No posts yet</p>
      <p className="text-xs text-light-subtle dark:text-dark-subtle">Content will appear here</p>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto"
      style={{ height: 'calc(100vh - 56px)', scrollbarWidth: 'none' }}
      onScroll={onScroll}
    >
      {/* latest strip */}
      <LatestStrip posts={posts} onSelect={onSelect} />

      {/* section label */}
      <div className="flex items-center justify-between px-4 mb-2">
        <span className="text-xs font-bold text-light-text dark:text-dark-text uppercase tracking-wider">All Posts</span>
        <span className="text-[10px] text-light-subtle dark:text-dark-subtle">{posts.length} posts</span>
      </div>

      {/* 3-column grid */}
      <div className="grid grid-cols-6 gap-px bg-light-border dark:bg-dark-border">
        {posts.map((post, i) => (
          <GridCard key={post.id} post={post} onClick={() => onSelect(i)} />
        ))}
      </div>

      {feedLoadingMore && (
        <div className="flex justify-center py-6">
          <Loader2 size={22} className="animate-spin text-primary dark:text-golden" />
        </div>
      )}
      {!feedPagination.has_more && posts.length > 0 && (
        <div className="text-center py-6">
          <span className="text-xs text-light-subtle dark:text-dark-subtle">You're all caught up 🎉</span>
        </div>
      )}
    </div>
  );
};

// ─── Main FeedPage ─────────────────────────────────────────────────────────────

export const FeedPage: React.FC = () => {
  const navigate = useNavigate();
  const { feedPosts, fetchFeed } = useFeedStore();
  const [detailIndex, setDetailIndex] = useState<number | null>(null);

  useEffect(() => { fetchFeed({ refresh: true }); }, []);

  return (
    <div className="bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text" style={{ height: 'calc(100vh - 56px)' }}>

      <GridOverview posts={feedPosts} onSelect={setDetailIndex} />

      {detailIndex !== null && (
        <DetailView
          posts={feedPosts}
          startIndex={detailIndex}
          onClose={() => setDetailIndex(null)}
        />
      )}

      {/* FAB */}
      <button
        onClick={() => navigate('/create-post')}
        className="fixed bottom-24 md:bottom-8 right-6 p-3.5 rounded-full bg-gradient-to-br from-primary to-golden text-white shadow-xl hover:scale-105 transition-transform z-40"
      >
        <PlusCircle size={22} />
      </button>
    </div>
  );
};

export default FeedPage;
